import React, { ReactNode } from 'react';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, MARKS } from '@contentful/rich-text-types';
import Heading from 'components/Text/Heading';
import Link from 'next/link';

import styles from './index.module.scss';

interface RichTextProps {
  content: Document;
}

export function RichText({ content }: RichTextProps) {
  const options = React.useMemo<Options>(
    () => ({
      renderMark: {
        [MARKS.BOLD]: (text: ReactNode) => <b>{text}</b>,
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children: ReactNode) => {
          return <p className={styles.paragraph}>{children}</p>;
        },
        [BLOCKS.HEADING_1]: node => {
          const plaintext = documentToPlainTextString(node);
          return <Heading level={1}>{plaintext}</Heading>;
        },
        [BLOCKS.HEADING_2]: node => {
          const plaintext = documentToPlainTextString(node);
          return <Heading level={2}>{plaintext}</Heading>;
        },
        [BLOCKS.HEADING_3]: node => {
          const plaintext = documentToPlainTextString(node);
          return <Heading level={3}>{plaintext}</Heading>;
        },
        [BLOCKS.HEADING_4]: node => {
          const plaintext = documentToPlainTextString(node);
          return <Heading level={4}>{plaintext}</Heading>;
        },
        [INLINES.HYPERLINK]: node => {
          const plaintext = documentToPlainTextString(node);

          return (
            <Link rel="noreferrer" href={node.data.uri} target="_blank">
              <a>{plaintext.trim()}</a>
            </Link>
          );
        },
      },
    }),
    [],
  );

  const RichText = documentToReactComponents(content, options);
  return <div className={styles.richTextBlock}>{RichText}</div>;
}
